// /security — the public Trust & Security page: one calm, scannable page a school trust can
// forward to its committee or IT reviewer. Shares the legal-page chrome (MarketingTopBar +
// SiteFooter + the sticky "On this page" rail) so it reads as part of the same system.
//
// EVERY claim on this page is grounded in the shipped product (CLAUDE.md "Security
// (non-negotiable)" + "DPDP machinery" + invariants I-08/I-09, docs/compliance/RETENTION_POLICY.md,
// docs/deployment/DEPLOYMENT_AWS.md). Don't add a claim here the codebase doesn't back.
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { MarketingTopBar, SiteFooter } from "../components/marketing/shared";
import { MarketingMotion } from "../components/marketing/MarketingMotion";
import MarketingSeo from "../components/marketing/MarketingSeo";
import BrandWordmarkText, { BrandCopy } from "../components/BrandWordmarkText";
import { SECURITY_EMAIL, SUPPORT_EMAIL } from "../lib/contactInfo";
import {
  ShieldCheck, Database, Sparkles, ScrollText, KeyRound, Landmark, Server, Mail, CheckCircle2, Printer,
} from "lucide-react";

// One date constant — bumped whenever this page is re-verified against the shipped product.
// TRUST-11: the trust pack is a PRINT OF THIS PAGE ("this page, verbatim, as of its review date"),
// so ANY edit here — not only a date bump — must regenerate it:
//     cd frontend && node scripts/build-trust-pack.mjs        (app running on :3000)
// That script reads REVIEWED_ON + TRUST_PACK.href below, refuses to run if the dated filename and
// the review month disagree, and writes public/trust/<name>.pdf. Bump BOTH constants together when
// the review month changes, so a new month never overwrites the previous month's artifact.
const REVIEWED_ON = "August 2026";
const TRUST_PACK = Object.freeze({
  href: "/trust/vidyapeeth360-trust-pack-2026-08.pdf",
  label: "Download the trust pack (PDF · August 2026)",
});

// PGS-8/TRUST-6 — the named provider/sub-processor disclosure (CLAIMS_REGISTRY S12). Every row is
// a provider this repository actually integrates; the wording keeps the registry conditions
// (I1–I3/I5/I7, S7, S13, DR1) attached. The consolidated list is published pending legal review
// (tracked in the marketing audit docs); rows must not be added without a claims-registry basis.
// The site-side row must stay in step with the cookie policy (core/legal_defaults.py) — that
// document names Google Analytics AND Microsoft Clarity, so this list does too.
const SUBPROCESSOR_ROWS = [
  ["Payment gateway", "Razorpay or Cashfree — the school's OWN connected account", "Payer identity, invoice references and payment evidence, held under the school's direct gateway agreement."],
  ["WhatsApp / SMS", "MSG91, and Meta's WhatsApp Cloud API where a school configures it", "Recipient phone numbers and school-approved template message content."],
  ["Email delivery", "SendGrid, or the school's own SMTP service", "Recipient email addresses and transactional message content."],
  ["Voice AI (optional)", "Sarvam AI, for the vernacular speech features where enabled", "Speech audio and its transcript for the requesting user's interaction, with identifier scrubbing applied to outbound text."],
  ["AI model provider (optional)", "The model provider configured for enabled Aira flows", "Identifier-redacted prompt text per the configured redaction path — the default router pattern-redacts common identifiers; name removal depends on the calling flow."],
  ["Cloud infrastructure", "Documented for each deployment before go-live (the reference architecture targets Amazon Web Services with CloudFront delivery)", "Hosting of the school's workspace — compute, database, storage and backup locations are deployment-specific and documented, never assumed from this website."],
  ["Website analytics and session replay", "Google Analytics and Microsoft Clarity — this marketing site only, each loaded only with visitor consent and only where that provider is configured for the deployment", "Anonymous marketing-site usage, and — for Clarity — anonymised interaction patterns such as clicks, scrolls and page structure. The school application itself is not in scope of this consent banner. Our analytics event contract excludes names, email addresses, phone numbers and student records."],
];

// Plain-language security sections. `points` are [bold lead, rest-of-sentence] pairs so every
// bullet is scannable in one glance.
const SECTIONS = [
  {
    id: "tenant-isolation",
    icon: Database,
    accent: "text-primary bg-primary/10",
    title: "Every school's data is walled off at the database layer",
    intro:
      "Each school on Vidyapeeth360 is a separate tenant, and the separation is enforced by the " +
      "database itself — PostgreSQL Row-Level Security — not just by application code. The database " +
      "refuses to return a row that doesn't belong to the school making the request.",
    points: [
      ["Fail-closed by design.", "If a request ever arrives without a school context, it matches nothing — an empty result, never another school's data."],
      ["Two independent walls.", "Application-level tenant filtering runs on top of the database-enforced isolation, so a bug in one layer is still caught by the other."],
      ["School groups are scoped too.", "A group administrator for an education trust sees only their own group's member schools — never another group's."],
    ],
  },
  {
    id: "ai-privacy",
    icon: Sparkles,
    accent: "text-ai-ink bg-ai/10",
    title: "AI with permission boundaries and configured privacy safeguards",
    intro:
      "Aira combines permission-scoped product tools, deterministic system evidence and optional " +
      "model-written explanations. The prompt is never an access-control boundary, and model-written " +
      "text must be checked against the linked source record when the decision matters.",
    points: [
      ["The default router pattern-redacts common identifiers.", "Phone numbers, emails, Aadhaar, PAN and UPI IDs are detected by configured patterns. Names are tokenized only when the calling context supplies known values, so every AI call path still requires review."],
      ["System evidence stays authoritative.", "Several assistants use deterministic reports or bounded numeric inputs. Model-written prose can still be wrong, so staff verify material figures and actions against the linked product record."],
      ["The verified student-AI health boundary is explicit.", "Current student-AI context excludes the named health, infirmary, wellbeing and counselling sources. That is a verified boundary for those paths, not a blanket promise for every present or future integration."],
      ["Permissions remain the access boundary.", "Assistant tools are expected to enforce tenant, role and record scope. A missing authorization check is treated as a security defect; a model instruction is never a substitute."],
    ],
  },
  {
    id: "consent-retention",
    icon: ScrollText,
    accent: "text-teal-dark bg-teal/10",
    title: "Consent, retention, and breach-response evidence",
    intro:
      "Vidyapeeth360 includes controls that can support a school's privacy programme as India's DPDP " +
      "framework comes into force in stages. Product controls do not determine the school's lawful " +
      "purpose, notice, consent validity, or legal retention period.",
    points: [
      ["An append-only consent ledger.", "Every consent grant, rejection, and withdrawal is a new immutable entry — database permissions prevent the application from editing or deleting past records. Withdrawing consent is as easy as granting it."],
      ["Classification-driven retention.", "Short-lived data such as notifications, messages, and consent links can be purged past configured windows; protected record classes require a governed retention or erasure review."],
      ["An internal 72-hour evidence timer.", "A recorded personal-data breach is tracked from open through notified to resolved. The school and its advisers still determine the legally required recipients, timing, content, and any extension."],
      ["Supported export and erasure paths.", "An authorised export can be prepared for review, and erasure redacts eligible personal fields while records held by configured retention rules remain restricted."],
    ],
  },
  {
    id: "access-control",
    icon: KeyRound,
    accent: "text-sky-dark bg-sky/10",
    title: "Access control inside the school",
    intro:
      "Isolation between schools is only half the story — inside a school, staff see only what their " +
      "role genuinely needs.",
    points: [
      ["Two-step sign-in for staff, MFA enforced for school administrators.", "Every staff login is password plus a one-time code, and multi-factor authentication is mandatory by policy for school and platform administrators."],
      ["Authenticator apps supported.", "Staff can enrol an offline TOTP authenticator app, with single-use backup codes — no dependence on SMS delivery for the second factor."],
      ["Role-based permissions, resolved fresh on every request.", "A revoked permission takes effect immediately — there is no stale session that keeps old access alive."],
      ["Teachers see their own classroom.", "Record-level scoping limits a classroom teacher to the class-sections they actually teach; guardian contact details are masked outside them, and student medical records are restricted to the nurse and administrators."],
      ["Separation of duties on money.", "Refunds, fee concessions, payroll, and payouts follow a maker-checker rule: the person who raises one can never approve their own."],
      ["An append-only audit trail.", "Privileged actions land in a tamper-evident audit log retained for 7 years — the application has no ability to delete it."],
    ],
  },
  {
    id: "money",
    icon: Landmark,
    accent: "text-orange-dark bg-orange-soft",
    title: "School-controlled payment collection",
    intro:
      "Fee collection runs through the school's connected payment-gateway account. Vidyapeeth360 " +
      "records provider evidence against the invoice; the school reconciles settlement timing, " +
      "deductions and final bank credit from provider or bank records.",
    points: [
      ["Invoice-bound provider evidence.", "A collection order and its verified callback remain tied to the invoice being paid."],
      ["A hash-chained payment audit log.", "Every payment event is appended to a log where each entry cryptographically seals the one before it — a retroactive edit is mathematically detectable."],
      ["Paid means provider-confirmed.", "An invoice is marked paid only on verified provider evidence — never because a checkout window closed. Final bank reconciliation remains with the school."],
      ["Exact to the paisa.", "Money is stored as whole paise end-to-end, and receipts are numbered sequentially from an atomic counter — no rounding drift, no duplicate receipt numbers."],
      ["A production safety guard.", "Outside of demo mode, the platform refuses to start with test-mode payment keys configured."],
    ],
  },
  {
    id: "operations",
    icon: Server,
    accent: "text-ink-2 bg-muted",
    title: "Operational security",
    intro: "The everyday disciplines behind everything above.",
    points: [
      ["Secrets are encrypted.", "Payment-gateway credentials and API secrets are encrypted at rest and managed through a cloud secrets manager — never stored in code."],
      ["Per-school webhook authentication.", "Each school's inbound payment and messaging webhooks are verified with that school's own HMAC-signed token, so one school's token can never be used against another."],
      ["Backup controls reviewed before go-live.", "Backup and recovery arrangements are documented for your production deployment before launch."],
      ["Deployment region is reviewed before go-live.", "Compute, database, storage and backup locations are deployment-specific and documented instead of being assumed from this website."],
    ],
  },
];

export default function Security() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <MarketingMotion>
    <div className="min-h-screen bg-canvas text-ink flex flex-col landing-page">
      <MarketingSeo
        title="Security & Data Protection Controls | Vidyapeeth360"
        description="Review Vidyapeeth360 tenant isolation, role-based access, encrypted gateway secrets, DPDP-supporting workflows and configured AI safeguards."
        path="/security"
      />
      <MarketingTopBar />
      <main className="flex-1">
        <div className="mx-auto max-w-6xl px-5 py-12 sm:py-16">
          {/* Header */}
          <div className="max-w-3xl">
            <p className="inline-flex items-center gap-2 rounded-full border border-line bg-surface px-3.5 py-1.5 text-[13px] font-semibold text-ink-2">
              <ShieldCheck className="w-4 h-4 text-primary" aria-hidden="true" /> Trust &amp; Security
            </p>
            <h1 className="mt-4 font-heading font-bold text-3xl sm:text-4xl text-ink tracking-tight">
              Your students' data, separated at the database — not just in the app.
            </h1>
            <p className="mt-4 text-ink-2 text-[16px] leading-relaxed">
              <BrandWordmarkText /> holds some of the most sensitive records an institution can — children's
              data within the scope of India's Digital Personal Data Protection Act, 2023. The Act
              and 2025 Rules have staged commencement. This page
              explains, in plain language, how the platform protects it: how schools are isolated
              from each other, what our AI can and cannot see, how consent and retention work, and
              how money moves. It describes product controls, not legal certification. Forward it
              to your trust, your committee, or your IT reviewer.
            </p>
            <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-ink-2">
              {[
                "DPDP-supporting controls, not certification",
                "Configured AI identifier safeguards",
                "Deployment region reviewed before go-live",
              ].map((t) => (
                <li key={t} className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-teal-dark shrink-0" aria-hidden="true" /> {t}
                </li>
              ))}
            </ul>
            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2">
              <p className="text-[13px] text-ink-subtle">Last reviewed against the shipped product: {REVIEWED_ON}.</p>
              <button type="button" onClick={() => window.print()} data-testid="security-print"
                className="print:hidden inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-3.5 py-1.5 text-[13px] font-semibold text-ink-2 hover:border-primary/40 hover:text-accent transition-colors focus:outline-none focus-visible:ring-2 ring-primary">
                <Printer className="w-3.5 h-3.5" aria-hidden="true" /> Print or save as PDF
              </button>
            </div>
          </div>

          {/* Mobile wayfinding — the desktop rail is lg-only and this page is long. */}
          <details className="lg:hidden print:hidden mt-8 rounded-2xl border border-line bg-surface p-4">
            <summary className="cursor-pointer text-sm font-semibold text-ink list-none flex items-center justify-between">
              On this page <span className="text-ink-subtle text-xs" aria-hidden="true">tap to open</span>
            </summary>
            <ul className="mt-3 space-y-2">
              {SECTIONS.map((sec) => (
                <li key={sec.id}>
                  <a href={`#${sec.id}`} className="block text-[14px] text-ink-2 hover:text-accent py-0.5 rounded focus:outline-none focus-visible:ring-2 ring-primary">{sec.title}</a>
                </li>
              ))}
              <li>
                <a href="#sub-processors" className="block text-[14px] text-ink-2 hover:text-accent py-0.5 rounded focus:outline-none focus-visible:ring-2 ring-primary">Who processes your data — and how to get our DPA</a>
              </li>
              <li>
                <a href="#report" className="block text-[14px] text-ink-2 hover:text-accent py-0.5 rounded focus:outline-none focus-visible:ring-2 ring-primary">Questions &amp; reporting</a>
              </li>
            </ul>
          </details>

          <div className="mt-10 lg:grid lg:grid-cols-[220px_1fr] lg:gap-12">
            {/* On-this-page rail — mirrors the legal pages */}
            <nav aria-label="On this page" className="mb-8 hidden self-start lg:sticky lg:top-[128px] lg:block">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-ink-subtle">On this page</p>
              <ul className="space-y-1.5 border-l border-line">
                {SECTIONS.map((s) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`}
                      className="-ml-px block border-l-2 border-transparent py-1 pl-3 text-[13px] leading-snug text-ink-subtle transition-colors hover:border-l-orange hover:text-ink focus-visible:outline-none focus-visible:text-ink">
                      {s.title}
                    </a>
                  </li>
                ))}
                <li>
                  <a href="#sub-processors"
                    className="-ml-px block border-l-2 border-transparent py-1 pl-3 text-[13px] leading-snug text-ink-subtle transition-colors hover:border-l-orange hover:text-ink focus-visible:outline-none focus-visible:text-ink">
                    Who processes your data
                  </a>
                </li>
                <li>
                  <a href="#report"
                    className="-ml-px block border-l-2 border-transparent py-1 pl-3 text-[13px] leading-snug text-ink-subtle transition-colors hover:border-l-orange hover:text-ink focus-visible:outline-none focus-visible:text-ink">
                    Questions & reporting
                  </a>
                </li>
              </ul>
            </nav>

            {/* Sections */}
            <div className="min-w-0 max-w-3xl space-y-12">
              {SECTIONS.map((s) => (
                <section key={s.id} id={s.id} aria-labelledby={`${s.id}-h`} className="scroll-mt-[128px]">
                  <div className="flex items-start gap-3.5">
                    <span className={`mt-0.5 w-10 h-10 rounded-xl grid place-items-center shrink-0 ${s.accent}`}>
                      <s.icon className="w-5 h-5" aria-hidden="true" />
                    </span>
                    <h2 id={`${s.id}-h`} className="font-heading font-semibold text-xl sm:text-[22px] text-ink leading-snug">
                      {s.title}
                    </h2>
                  </div>
                  <p className="mt-3 text-[15px] text-ink-2 leading-relaxed sm:pl-[3.375rem]"><BrandCopy>{s.intro}</BrandCopy></p>
                  <ul className="mt-4 space-y-3 sm:pl-[3.375rem]">
                    {s.points.map(([lead, rest]) => (
                      <li key={lead} className="flex gap-2.5 text-[15px] leading-relaxed">
                        <CheckCircle2 className={`w-4 h-4 mt-1 shrink-0 ${s.accent.split(" ")[0]}`} aria-hidden="true" />
                        <span className="text-ink-2"><span className="font-semibold text-ink">{lead}</span> {rest}</span>
                      </li>
                    ))}
                  </ul>
                  {/* A supported redaction example. Names are tokenized only when the calling flow
                      supplies known values; the caption keeps that condition visible. */}
                  {s.id === "ai-privacy" && (
                    <figure className="mt-5 sm:ml-[3.375rem] rounded-2xl border border-ai/25 bg-ai/[0.04] p-4 sm:p-5" data-testid="security-redaction-exhibit">
                      <figcaption className="text-[11px] font-bold uppercase tracking-wide text-ai-ink mb-3">One configured redaction path</figcaption>
                      <div className="space-y-2.5 font-mono text-[12.5px] sm:text-[13px] leading-relaxed">
                        <p className="text-ink-2">
                          <span className="block text-[10.5px] font-sans font-semibold uppercase tracking-wide text-ink-subtle mb-0.5">Your accountant asks</span>
                          “Has <span className="text-ink font-semibold">Ananya Krishnan</span> (98400 12345) cleared the ₹18,500 term fee?”
                        </p>
                        <p className="text-ink-2">
                          <span className="block text-[10.5px] font-sans font-semibold uppercase tracking-wide text-ink-subtle mb-0.5">The AI provider receives</span>
                          “Has <span className="text-ai-ink font-semibold">[NAME_1]</span> (<span className="text-ai-ink font-semibold">[PHONE_1]</span>) cleared the ₹18,500 term fee?”
                        </p>
                      </div>
                      <p className="mt-3 text-[12.5px] font-sans text-ink-subtle">The phone pattern is detected by default; the name token requires the calling flow to supply that known value. Example uses sample data.</p>
                    </figure>
                  )}
                </section>
              ))}

              {/* PGS-8/TRUST-6 — sub-processor disclosure + the DPA request channel. */}
              <section id="sub-processors" aria-labelledby="sub-processors-h" className="scroll-mt-[128px]" data-testid="security-subprocessors">
                <div className="flex items-start gap-3.5">
                  <span className="mt-0.5 w-10 h-10 rounded-xl grid place-items-center shrink-0 text-teal-dark bg-teal/10">
                    <Server className="w-5 h-5" aria-hidden="true" />
                  </span>
                  <h2 id="sub-processors-h" className="font-heading font-semibold text-xl sm:text-[22px] text-ink leading-snug">
                    Who processes your data — and how to get our DPA
                  </h2>
                </div>
                <p className="mt-3 text-[15px] text-ink-2 leading-relaxed sm:pl-[3.375rem]">
                  A school's deployment can involve the named service providers below, each limited
                  to the role and data category shown. Which of them actually apply depends on what
                  your school connects and enables — several are optional, and the payment gateway
                  runs under the school's own provider agreement.
                </p>
                <div className="mt-4 sm:pl-[3.375rem]">
                  <ul className="divide-y divide-line rounded-2xl border border-line bg-surface">
                    {SUBPROCESSOR_ROWS.map(([category, provider, scope]) => (
                      <li key={category} className="p-4">
                        <p className="text-[13px] font-bold uppercase tracking-wide text-ink-subtle">{category}</p>
                        <p className="mt-1 text-[14.5px] font-semibold text-ink leading-snug">{provider}</p>
                        <p className="mt-1 text-[13.5px] text-ink-2 leading-relaxed">{scope}</p>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-4 text-[14.5px] text-ink-2 leading-relaxed">
                    <span className="font-semibold text-ink">Need it contractually?</span>{" "}
                    Request our Data Processing Agreement:{" "}
                    <a href="mailto:legal@helprevx.com" className="font-medium text-accent hover:underline rounded focus:outline-none focus-visible:ring-2 ring-primary">legal@helprevx.com</a>.
                    The executed DPA and your written proposal govern the final provider list for
                    your school's deployment.
                  </p>
                  <p className="mt-3 text-[13px] text-ink-subtle leading-relaxed">
                    Running the DPDP lens over this page? The{" "}
                    <Link to="/compliance/dpdp" className="font-medium text-accent hover:underline rounded focus:outline-none focus-visible:ring-2 ring-primary" data-testid="security-dpdp-link">DPDP guide for schools</Link>{" "}
                    maps each privacy duty to its supporting control.
                  </p>
                </div>
              </section>

              {/* Contact / responsible disclosure */}
              <section id="report" aria-labelledby="report-h" className="scroll-mt-[128px] rounded-2xl border border-line bg-surface p-6 sm:p-7">
                <div className="flex items-start gap-3.5">
                  <span className="mt-0.5 w-10 h-10 rounded-xl grid place-items-center shrink-0 text-primary bg-primary/10">
                    <Mail className="w-5 h-5" aria-hidden="true" />
                  </span>
                  <h2 id="report-h" className="font-heading font-semibold text-xl text-ink leading-snug">
                    Questions — or something to report?
                  </h2>
                </div>
                <p className="mt-3 text-[15px] text-ink-2 leading-relaxed">
                  Running a security review for your committee? Email{" "}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="font-medium text-accent hover:underline rounded focus:outline-none focus-visible:ring-2 ring-primary">{SUPPORT_EMAIL}</a>{" "}
                  and we'll work through your questionnaire with you.
                </p>
                {/* TRUST-11 — the filable artifact: this page, versioned and dated, as a PDF. */}
                <p className="mt-3 text-[15px] text-ink-2 leading-relaxed">
                  Need something to file?{" "}
                  <a href={TRUST_PACK.href} className="font-medium text-accent hover:underline rounded focus:outline-none focus-visible:ring-2 ring-primary" data-testid="security-trust-pack">
                    {TRUST_PACK.label}
                  </a>
                  {" "}— this page, verbatim, as of its review date ({REVIEWED_ON}). The print button
                  above always produces the current copy.
                </p>
                <p className="mt-3 text-[15px] text-ink-2 leading-relaxed">
                  If you believe you've found a security vulnerability, email{" "}
                  <a href={`mailto:${SECURITY_EMAIL}`} className="font-medium text-accent hover:underline rounded focus:outline-none focus-visible:ring-2 ring-primary">{SECURITY_EMAIL}</a>{" "}
                  with “Security” in the subject line. We read, acknowledge, and investigate every
                  good-faith report.
                </p>
                <p className="mt-4 text-sm text-ink-subtle">
                  See also our{" "}
                  <Link to="/privacy-policy" className="font-medium text-accent hover:underline rounded focus:outline-none focus-visible:ring-2 ring-primary">Privacy Policy</Link>
                  {" "}or{" "}
                  <Link to="/contact" className="font-medium text-accent hover:underline rounded focus:outline-none focus-visible:ring-2 ring-primary">contact us</Link>
                  {" "}for anything else.
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
    </MarketingMotion>
  );
}
